import React, { useState, useEffect } from 'react';
import { API, LOGO_URL } from '../utils/constants';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MountainSnow, Castle, Waves, ArrowLeft, LogIn, Loader2, User, Lock,
  Bug, ClipboardList, Code2, UserPlus, FileText, Flame, Wine, Truck, Send,
} from 'lucide-react';
import ProblemReportModal from './ProblemReportModal';
import MattersFab from './MattersFab';
import RegistrationForm from './RegistrationForm';
import EmployeeFormModal from './EmployeeFormModal';
import QuickLossModal from './QuickLossModal';
import LegalIconMenu from './legal/LegalIconMenu';
import QuickGlassLossModal from './QuickGlassLossModal';
import JerryDrivePublic from './public/JerryDrivePublic';
import JerryDriveAccessLock from './public/JerryDriveAccessLock';
import LoginScreenLegacy from './LoginScreen';
import PortalOverlay from './common/PortalOverlay';
import './LoginScreenLuxe.css';

// Small helper to reset a one-shot open trigger after MattersFab consumes it
const MattersOpenResetter = ({ onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 200);
    return () => clearTimeout(t);
  }, [onDone]);
  return null;
};

/**
 * MR JERRY premium auth flow — 2 steps:
 *   1) Location picker (Nowy Sącz / Kraków / Gródek n/D)
 *   2) Login form (username+password OR "Zaloguj przez Google")
 * Google Auth: Emergent-managed. On successful auth, backend maps Google email → existing user account.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */

const LOCATIONS = [
  { id: 'nowysacz',  name: 'Nowy Sącz',            Icon: MountainSnow },
  { id: 'krakow',    name: 'Kraków',               Icon: Castle },
  { id: 'grodek',    name: 'Gródek nad Dunajcem',  Icon: Waves },
];

const LAST_LOCATION_KEY = 'mrjerry_last_location';

const LoginScreenLuxe = ({ onLogin }) => {
  const [step, setStep] = useState('location');   // 'location' | 'login' | 'exchanging'
  const [selectedLocation, setSelectedLocation] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  // FAB modals
  const [showProblemReport, setShowProblemReport] = useState(false);
  const [showMatters, setShowMatters] = useState(false);
  const [showMattersToHQ, setShowMattersToHQ] = useState(false);
  const [devMode, setDevMode] = useState(false);
  // Additional login-screen modals (from legacy)
  const [showRegistration, setShowRegistration] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showQuickLoss, setShowQuickLoss] = useState(false);
  const [showQuickGlassLoss, setShowQuickGlassLoss] = useState(false);
  const [showJerryDrive, setShowJerryDrive] = useState(false);
  const [jdLock, setJdLock] = useState({ open: false, type: null, error: '' });

  // Handle OAuth return: URL hash #session_id=XXX
  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) return;

    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    setStep('exchanging');
    (async () => {
      try {
        const r = await fetch(`${API}/auth/google/callback`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || 'Logowanie Google się nie powiodło');
        }
        const data = await r.json();
        // Clean URL fragment
        window.history.replaceState({}, document.title, window.location.pathname);
        // Remember location for next time
        try { localStorage.setItem(LAST_LOCATION_KEY, data.user.location); } catch { /* ignore */ }
        toast.success(`Zalogowano jako ${data.user.username}`);
        onLogin(data.user, data.user.location);
      } catch (err) {
        setError(err.message);
        setStep('location');
      }
    })();
  }, [onLogin]);

  const handleLocationPick = (locId) => {
    setSelectedLocation(locId);
    setError('');
    setStep('login');
  };

  const handleBack = () => {
    setError('');
    setUsername('');
    setPassword('');
    setStep('location');
  };

  const handleClassicLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Podaj login i hasło');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password, location: selectedLocation }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Nieprawidłowe dane logowania');
      try { localStorage.setItem(LAST_LOCATION_KEY, selectedLocation); } catch { /* ignore */ }
      toast.success(`Witaj, ${data.user.username}`);
      onLogin(data.user, selectedLocation);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Jerry Drive unlock — check code type first (pin/gesture), then verify
  const handleJerryDriveClick = async () => {
    try {
      const r = await axios.get(`${API}/jerry-drive/access?location=nowysacz`);
      if (r.data?.isSet) {
        setJdLock({ open: true, type: r.data.type || 'pin', error: '' });
      } else {
        setShowJerryDrive(true);
      }
    } catch {
      setShowJerryDrive(true);
    }
  };
  const handleJdVerify = async (value) => {
    setJdLock((s) => ({ ...s, error: '' }));
    try {
      const r = await axios.post(`${API}/jerry-drive/access/verify?location=nowysacz`, { value });
      if (r.data?.valid) {
        setJdLock({ open: false, type: null, error: '' });
        setShowJerryDrive(true);
      } else {
        setJdLock((s) => ({ ...s, error: 'Niepoprawny kod / gest' }));
      }
    } catch {
      setJdLock((s) => ({ ...s, error: 'Błąd weryfikacji' }));
    }
  };

  // ===== RENDER =====
  if (devMode) {
    return <LoginScreenLegacy onLogin={onLogin} autoOpenDevPanel onDevPanelClose={() => setDevMode(false)} />;
  }

  if (step === 'exchanging') {
    return (
      <div className="lx-auth-root" data-testid="login-exchanging">
        <div className="lx-radial-glow" />
        <div className="lx-card lx-card--center">
          <Loader2 size={40} className="lx-spin" />
          <div className="lx-loading-title">Logowanie przez Google…</div>
          <div className="lx-loading-sub">Weryfikujemy Twój adres email</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lx-auth-root" data-testid="login-screen-luxe">
      <div className="lx-radial-glow" />
      <div className="lx-noise-overlay" />

      <div className="lx-card">
        {/* Brand */}
        <div className="lx-brand">
          <div className="lx-logo-ring">
            <img
              src={LOGO_URL}
              alt="MR JERRY"
              className="lx-logo-img"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="lx-brand-eyebrow">System zarządzania</div>
          <h1 className="lx-brand-name">MR JERRY</h1>
          <div className="lx-brand-tagline">Nowy Sącz &nbsp;·&nbsp; Kraków &nbsp;·&nbsp; Gródek n/D</div>
        </div>

        {/* Step 1: Location picker */}
        {step === 'location' && (
          <div className="lx-step" data-testid="login-step-location">
            <div className="lx-step-label">Wybierz lokal</div>
            <div className="lx-locations">
              {LOCATIONS.map((loc) => {
                const Icon = loc.Icon;
                return (
                  <button
                    key={loc.id}
                    type="button"
                    className="lx-loc-card"
                    onClick={() => handleLocationPick(loc.id)}
                    data-testid={`location-card-${loc.id}`}
                  >
                    <Icon size={28} strokeWidth={1.6} className="lx-loc-icon" />
                    <div className="lx-loc-name">{loc.name}</div>
                  </button>
                );
              })}
            </div>

            {/* Google login on step 1 too — quick access */}
            <div className="lx-divider"><span>lub</span></div>
            <button
              type="button"
              className="lx-google-btn"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-btn"
            >
              {googleLoading ? (
                <Loader2 size={18} className="lx-spin" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
              )}
              <span>Zaloguj przez Google</span>
            </button>
            <div className="lx-google-hint">
              Twój email musi być zarejestrowany w module Manager
            </div>
          </div>
        )}

        {/* Step 2: Login form */}
        {step === 'login' && (
          <div className="lx-step" data-testid="login-step-credentials">
            <button
              type="button"
              className="lx-back"
              onClick={handleBack}
              data-testid="login-back"
            >
              <ArrowLeft size={14} strokeWidth={2} /> Zmień lokal
            </button>

            <form onSubmit={handleClassicLogin} className="lx-form">
              <label className="lx-field">
                <User size={14} className="lx-field-icon" strokeWidth={1.8} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Login"
                  autoComplete="username"
                  data-testid="login-username"
                  autoFocus
                />
              </label>
              <label className="lx-field">
                <Lock size={14} className="lx-field-icon" strokeWidth={1.8} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Hasło"
                  autoComplete="current-password"
                  data-testid="login-password"
                />
              </label>

              {error && (
                <div className="lx-error" data-testid="login-error">{error}</div>
              )}

              <button
                type="submit"
                className="lx-submit"
                disabled={submitting}
                data-testid="login-submit"
              >
                {submitting ? <Loader2 size={16} className="lx-spin" /> : <LogIn size={16} strokeWidth={2} />}
                {submitting ? 'Logowanie…' : 'Zaloguj się'}
              </button>
            </form>

            <div className="lx-divider"><span>lub</span></div>

            <button
              type="button"
              className="lx-google-btn"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-btn-form"
            >
              {googleLoading ? (
                <Loader2 size={18} className="lx-spin" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
              )}
              <span>Zaloguj przez Google</span>
            </button>

            {/* Action buttons — form / register */}
            <div className="lx-action-row">
              <button
                type="button"
                className="lx-action-btn"
                onClick={() => setShowRegistration(true)}
                data-testid="login-register-btn"
              >
                <UserPlus size={16} strokeWidth={1.8} />
                Załóż konto
              </button>
              <button
                type="button"
                className="lx-action-btn"
                onClick={() => setShowEmployeeForm(true)}
                data-testid="login-form-btn"
              >
                <FileText size={16} strokeWidth={1.8} />
                Formularz
              </button>
            </div>

            {/* Legal / RODO — linki wymagane prawnie na ekranie logowania */}
            <div
              data-testid="login-legal-links"
              style={{
                marginTop: 14,
                padding: '14px 16px 12px',
                background: 'rgba(212, 175, 55, 0.05)',
                border: '1px solid rgba(212, 175, 55, 0.18)',
                borderRadius: 12,
                fontSize: 11.5,
                color: 'rgba(255, 255, 255, 0.72)',
                lineHeight: 1.55,
                textAlign: 'center',
              }}
            >
              <div style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'rgba(212,175,55,0.7)',
                marginBottom: 10,
                fontWeight: 600,
              }}>
                Logując się akceptujesz
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                {[
                  { href: '/regulamin', label: 'Regulamin' },
                  { href: '/polityka-prywatnosci', label: 'Politykę prywatności' },
                  { href: '/polityka-cookies', label: 'Politykę cookies' },
                ].map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: '#d4af37',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: 11.5,
                      padding: '5px 10px',
                      borderRadius: 999,
                      background: 'rgba(212, 175, 55, 0.10)',
                      border: '1px solid rgba(212, 175, 55, 0.25)',
                      whiteSpace: 'nowrap',
                      transition: 'background 0.15s ease, border-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(212, 175, 55, 0.20)';
                      e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(212, 175, 55, 0.10)';
                      e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.25)';
                    }}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: '1px dashed rgba(255,255,255,0.08)',
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.42)',
                  lineHeight: 1.55,
                  letterSpacing: 0.1,
                }}
              >
                <div style={{ color: 'rgba(255,255,255,0.68)', fontWeight: 600, fontSize: 10.5, marginBottom: 2 }}>
                  MR JERRY JERZY FLOREK
                </div>
                ul. Kościuszki 18, 33-300 Nowy Sącz &nbsp;·&nbsp; NIP 734-350-41-81
              </div>
            </div>

            {/* Quick employee actions — 3 circles (Jerry Drive ONLY for Nowy Sącz) */}
            <div className="lx-quick-circles">
              <button
                type="button"
                className="lx-quick-circle lx-quick-circle--red"
                onClick={() => setShowQuickLoss(true)}
                title="Zgłoś stratę kuchenną"
                data-testid="login-quick-loss"
              >
                <Flame size={22} strokeWidth={1.8} />
                <span>Strata kuchenna</span>
              </button>
              <button
                type="button"
                className="lx-quick-circle lx-quick-circle--orange"
                onClick={() => setShowQuickGlassLoss(true)}
                title="Zgłoś stratę szkła"
                data-testid="login-quick-glass"
              >
                <Wine size={22} strokeWidth={1.8} />
                <span>Strata szkła</span>
              </button>
              {selectedLocation === 'nowysacz' && (
                <button
                  type="button"
                  className="lx-quick-circle lx-quick-circle--blue"
                  onClick={handleJerryDriveClick}
                  title="Jerry Drive"
                  data-testid="login-quick-jerrydrive"
                >
                  <Truck size={22} strokeWidth={1.8} />
                  <span>Jerry Drive</span>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="lx-footer">
          <span>© 2026 MR JERRY</span>
        </div>
      </div>

      {/* Floating action buttons — top-right: problem + matters (same style) */}
      <div className="lx-fab-cluster" data-testid="login-fab-cluster">
        <button
          type="button"
          className="lx-fab lx-fab--red"
          onClick={() => setShowProblemReport(true)}
          title="Zgłoś problem techniczny"
          aria-label="Zgłoś problem"
          data-testid="login-fab-problem"
        >
          <Bug size={22} strokeWidth={1.8} />
        </button>
        <button
          type="button"
          className="lx-fab lx-fab--gold"
          onClick={() => setShowMatters(true)}
          title="Zgłoś sprawę do managera"
          aria-label="Zgłoś sprawę"
          data-testid="login-fab-matters"
        >
          <ClipboardList size={22} strokeWidth={1.8} />
        </button>
        {/* Cross-location FAB — TYLKO dla Krakowa: zgłoszenie do właściciela w NS
            WYŁĄCZONE tymczasowo (żeby uniknąć zbyt wielu ikon na ekranie logowania).
            Backend + filter w module Sprawy działają — wystarczy zmienić false na true. */}
        {false && selectedLocation === 'krakow' && (
          <button
            type="button"
            className="lx-fab"
            onClick={() => setShowMattersToHQ(true)}
            title="Zgłoś sprawę do właściciela (Nowy Sącz)"
            aria-label="Zgłoszenie do właściciela"
            data-testid="login-fab-matters-hq"
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
              boxShadow: '0 8px 24px rgba(139, 92, 246, 0.35)',
              color: '#fff',
            }}
          >
            <Send size={22} strokeWidth={1.8} />
          </button>
        )}
      </div>

      {/* Dev panel button (bottom-left) */}
      <button
        type="button"
        className="lx-fab lx-fab--dev"
        onClick={() => setDevMode(true)}
        title="Panel programisty"
        aria-label="Panel programisty"
        data-testid="login-fab-dev"
      >
        <Code2 size={22} strokeWidth={1.8} />
      </button>

      {/* Modals — portalowane, żeby się otwierały jako osobne okna nad ekranem logowania */}
      {showProblemReport && (
        <PortalOverlay onClose={() => setShowProblemReport(false)}>
          <ProblemReportModal
            isOpen={showProblemReport}
            onClose={() => setShowProblemReport(false)}
            source="login-screen"
            location={selectedLocation}
          />
        </PortalOverlay>
      )}
      {/* Headless MattersFab — button hidden, triggered by our custom "matters" FAB */}
      <MattersFab
        anonymous
        location={selectedLocation || 'nowysacz'}
        hideDefaultButton
        externalOpen={showMatters}
      />
      {/* Cross-location MattersFab — sprawa idzie do NS z sourceLocation=krakow */}
      <MattersFab
        anonymous
        location={selectedLocation || 'krakow'}
        hideDefaultButton
        externalOpen={showMattersToHQ}
        crossLocationTarget="nowysacz"
      />
      {showMattersToHQ && (
        <MattersOpenResetter onDone={() => setShowMattersToHQ(false)} />
      )}
      {/* Reset trigger after MattersFab opens so a re-click works */}
      {showMatters && (
        <MattersOpenResetter onDone={() => setShowMatters(false)} />
      )}

      {/* Legacy modals from old LoginScreen — renderowane przez portal, żeby były PEŁNYM osobnym oknem nad ekranem logowania, a nie renderowały się "obok" niego */}
      {showRegistration && selectedLocation && (
        <PortalOverlay onClose={() => setShowRegistration(false)}>
          <div
            className="registration-form-overlay"
            style={{ position: 'fixed', inset: 0, zIndex: 100000 }}
            onClick={() => setShowRegistration(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <RegistrationForm
                location={selectedLocation}
                onClose={() => setShowRegistration(false)}
                onSuccess={() => {
                  setShowRegistration(false);
                  toast.success('Konto utworzone. Możesz się zalogować.');
                }}
              />
            </div>
          </div>
        </PortalOverlay>
      )}
      {showEmployeeForm && selectedLocation && (
        <PortalOverlay onClose={() => setShowEmployeeForm(false)}>
          <div
            className="registration-form-overlay"
            style={{ position: 'fixed', inset: 0, zIndex: 100000 }}
            onClick={() => setShowEmployeeForm(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <EmployeeFormModal
                location={selectedLocation}
                onClose={() => setShowEmployeeForm(false)}
              />
            </div>
          </div>
        </PortalOverlay>
      )}
      {showQuickLoss && (
        <PortalOverlay onClose={() => setShowQuickLoss(false)}>
          <QuickLossModal
            isOpen={showQuickLoss}
            onClose={() => setShowQuickLoss(false)}
            location={selectedLocation}
          />
        </PortalOverlay>
      )}
      <QuickGlassLossModal
        isOpen={showQuickGlassLoss}
        onClose={() => setShowQuickGlassLoss(false)}
        location={selectedLocation}
      />
      {jdLock.open && (
        <PortalOverlay onClose={() => setJdLock({ open: false, type: null, error: '' })}>
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setJdLock({ open: false, type: null, error: '' }); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.94)',
              backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
              zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
            }}
            data-testid="jd-lock-portal-overlay"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(180deg, rgba(17,24,39,0.98), rgba(2,6,23,0.98))',
                borderRadius: 20, padding: 24, maxWidth: 420, width: '100%',
                border: '1px solid rgba(6,182,212,0.25)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
              }}
            >
              <JerryDriveAccessLock
                mode="enter"
                allowedType={jdLock.type}
                title="🚗 Jerry Drive — kod dostępu"
                errorMsg={jdLock.error}
                onCancel={() => setJdLock({ open: false, type: null, error: '' })}
                onSuccess={handleJdVerify}
              />
            </div>
          </div>
        </PortalOverlay>
      )}
      {showJerryDrive && (
        <PortalOverlay onClose={() => setShowJerryDrive(false)}>
          <JerryDrivePublic onClose={() => setShowJerryDrive(false)} />
        </PortalOverlay>
      )}

      {/* Floating legal icon — zawsze dostępny na loginie */}
      <LegalIconMenu position="bottom-right" />
    </div>
  );
};

export default LoginScreenLuxe;
